import { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';

/**
 * Component that parses text for email references like "(email #uuid)"
 * and replaces them with clickable buttons showing the email subject
 */
export default function EmailReferencesText({ text }) {
  const [parsedContent, setParsedContent] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    parseText();
  }, [text]);

  const parseText = async () => {
    if (!text) {
      setParsedContent([{ type: 'text', content: '' }]);
      return;
    }

    // Regex to match both (email #uuid) and #uuid patterns
    const emailRefPattern = /(?:\(email #([a-f0-9-]{36})\)|#([a-f0-9-]{36}))/gi;
    const parts = [];
    let lastIndex = 0;
    let match;

    // Find all email references
    const matches = [];
    while ((match = emailRefPattern.exec(text)) !== null) {
      matches.push({
        fullMatch: match[0],
        uuid: match[1] || match[2], // First group for (email #uuid), second for #uuid
        index: match.index
      });
    }

    // If no matches, return text as-is
    if (matches.length === 0) {
      setParsedContent([{ type: 'text', content: text }]);
      return;
    }

    // Fetch all referenced emails
    const emailPromises = matches.map(m =>
      api.get(`/emails/${m.uuid}`).catch(() => null)
    );
    const emailResponses = await Promise.all(emailPromises);
    const emails = emailResponses.map(r => r?.data || null);

    // Build parts array with text and email references
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const email = emails[i];

      // Add text before this match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }

      // Add email reference
      parts.push({
        type: 'email',
        uuid: match.uuid,
        subject: email?.subject || 'Unknown Email',
        from: email?.from
      });

      lastIndex = match.index + match.fullMatch.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }

    setParsedContent(parts);
  };

  const handleEmailClick = (uuid) => {
    navigate(`/emails?id=${uuid}`);
  };

  const truncateSubject = (subject, maxLength = 40) => {
    if (!subject) return 'Unknown';
    if (subject.length <= maxLength) return subject;
    return subject.substring(0, maxLength) + '...';
  };

  return (
    <div className="whitespace-pre-wrap">
      {parsedContent.map((part, idx) => {
        if (part.type === 'text') {
          return <span key={idx}>{part.content}</span>;
        } else {
          return (
            <button
              key={idx}
              onClick={() => handleEmailClick(part.uuid)}
              className="inline-flex items-center gap-1 px-2 py-0.5 mx-1 text-xs font-medium rounded bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:hover:bg-blue-900 transition-colors"
              title={`${part.subject}\nFrom: ${part.from}`}
            >
              <Mail className="h-3 w-3 flex-shrink-0" />
              <span className="truncate max-w-[200px]">
                {truncateSubject(part.subject)}
              </span>
            </button>
          );
        }
      })}
    </div>
  );
}
